<script lang="ts">
	import HeadlessBadger from '../badger/HeadlessBadger.svelte'
	import { replaceUrl } from '$utils/misc'
	import { onDestroy, onMount } from 'svelte'
	import { getJwt, jwt } from '$utils/jwt'
	import LoadingSkeleton from '../animations/LoadingSkeleton.svelte'
	import Basic from '../templates/Basic.svelte'
	import { hideAnimation, manualOpenWhitelist, showAnimation } from '../stores'
	import { fetchUtils } from '$utils/fetchUtils'
	import LoadingAnimations from '../organisms/LoadingAnimations.svelte'
	import { LoadingAnimation } from '$types/enums'
	import PlatformRequestModal from '../map/platformRequests/PlatformRequestModal.svelte'
	import HubSpotEngagementModal from '../hubspot/HubSpotEngagementModal.svelte'
	import type { HubSpotEngagementType } from '../hubspot/HubSpotEngagement.model'

	let frameSrc: string
	let helpBoxOpen: boolean = false

	let iFrameLoaded: boolean = false

	let iFrameUrl: string = ''

	let reqLotId: string | null = null
	let reqPopId: string | null = null

	let platformRequestModal: PlatformRequestModal | undefined

	let hubspotRecordId: string | null = null
	let hubspotEngagementType: HubSpotEngagementType = 'call'
	let hubspotEngagementModal: HubSpotEngagementModal | undefined

	function setIFrameInfoAfterLoad() {
		iFrameLoaded = true
		const mainFrame = top?.document?.getElementById('mainFrame') as HTMLIFrameElement | null
		if (mainFrame) {
			iFrameUrl = mainFrame.src
		}
	}

	// TODO: IS JB since we proxy /app in dev as well, this might have become obsolete
	// prove, test, deprecate
	function getHrefReplaced() {
		// HINT: proven during DigDok sprint Q1 2026 in MF-593
		// in dev, we can keep the same origin (localhost:5000) so
		// the iframe goes through Vite's proxy,
		// avoiding cross-origin errors between :5000 and :9000
		// HINT: only works together with the dev config in vite.config.ts
		return window.location.href
	}

	function handlePostMessages(e: MessageEvent) {
		if (e.origin !== window.location.origin || !e.data.type) {
			//FIXME is this secure ??
			return
		}

		// noinspection FallThroughInSwitchStatementJS This is intentional
		switch (e.data.type) {
			case 'iframeInfo':
				updateUrl(e.data.content)
				break
			case 'iframeClick':
				window.dispatchEvent(new CustomEvent('iframeClick'))
			//disable-next-line no-fall-through fallthrough intended
			case 'iframeTouch':
			case 'iframeKey':
				window.dispatchEvent(new CustomEvent('iframeActivity'))
				break
			case 'openWhitelist':
				$manualOpenWhitelist = true
				break
		}
	}

	function updateUrl(data: { path: string; search: string }) {
		let newPath = data.path.replace('/app/', '/')
		let search = data.search
		if (newPath.endsWith('/')) {
			newPath = newPath.slice(0, -1)
		}

		checkIds()
		if (
			window.top &&
			window.location.pathname !== newPath &&
			window.top.document.location.pathname !== newPath
		) {
			replaceUrl(newPath, window.top, new URLSearchParams(search))
		}
		helpBoxOpen = false // Close help modal when url changes
	}

	function checkIds() {
		let fr: HTMLIFrameElement | null = top?.document.getElementById(
			'mainFrame'
		) as HTMLIFrameElement
		if (fr) {
			let frDoc: Document | undefined = fr.contentDocument || fr.contentWindow?.document
			if (frDoc) {
				let uid = (frDoc.getElementsByName('user-id')[0] as HTMLInputElement)?.value || ''
				let tid = (frDoc.getElementsByName('tenant-id')[0] as HTMLInputElement)?.value || ''
				let spyuid = (frDoc.getElementsByName('spy-user-id')[0] as HTMLInputElement)?.value || ''
				let spytid = (frDoc.getElementsByName('spy-tenant-id')[0] as HTMLInputElement)?.value || ''
				let ids = { uid, spyuid, tid, spytid }
				if (!isJWTOutdated(ids)) {
					return
				}
			}
		}
		getJwt()
	}

	function isJWTOutdated(ids: { uid: string; spyuid: string; tid: string; spytid: string }) {
		for (const [key, value] of Object.entries(ids)) {
			if (value && $jwt[key] !== value) {
				return true
			}
		}
		return false
	}

	function openPlatformRequestModal(e: Event) {
		const detail = (e as CustomEvent).detail
		reqLotId = detail.lotId
		reqPopId = detail.popId
		platformRequestModal?.openPlatformRequestModal()
	}

	function openHubspotEngagementModal(e: Event) {
		const detail = (e as CustomEvent).detail
		hubspotRecordId = detail.recordId
		hubspotEngagementType = detail.type ?? 'call'
		hubspotEngagementModal?.openHubspotEngagementModal()
	}

	// this function fetches parameters of a post request which are cached. it's needed since we
	// lose our post parameters by redirecting from svelte to a sirius iFrame
	// more in SvelteController.java
	async function fetchCachedRequestAndSubmitForm(requestId: string) {
		await fetchUtils
			.get('/api/cachedRequest/' + requestId)
			.then((data) => {
				let hiddenForm = top?.document?.createElement('form')
				if (hiddenForm) {
					hiddenForm.id = 'cachedRequestForm'
					hiddenForm.name = 'cachedRequestForm'
					hiddenForm.method = 'post'
					hiddenForm.target = 'mainFrame'
					hiddenForm.action = '/app' + window.location.pathname

					Object.keys(data).forEach((key) => {
						let input = top?.document?.createElement('input')
						if (input) {
							input.name = key
							input.value = data[key]
							input.type = 'hidden'

							hiddenForm.appendChild(input)
						}
					})

					document.body.appendChild(hiddenForm)

					hiddenForm.submit()
				}
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
	}

	const onShowMmLoader = () => showAnimation(LoadingAnimation.MmLoader)
	const onHideMmLoader = () => hideAnimation(LoadingAnimation.MmLoader)
	const onShowFileUpload = () => showAnimation(LoadingAnimation.FileUpload)
	const onHideFileUpload = () => hideAnimation(LoadingAnimation.FileUpload)
	const onShowAiUpload = () => showAnimation(LoadingAnimation.AiUpload)
	const onHideAiUpload = () => hideAnimation(LoadingAnimation.AiUpload)

	onMount(async () => {
		/**
		 * register listeners up front, before the async iframe setup below — otherwise a component
		 * destroyed while the await is pending would leave onDestroy removing listeners that were
		 * never added, and early openPlatformRequest/openHubspotEngagement events would be missed
		 */
		document.addEventListener('showLoadingAnimation', onShowMmLoader)
		document.addEventListener('hideLoadingAnimation', onHideMmLoader)
		document.addEventListener('showFileUploadLoadingAnimation', onShowFileUpload)
		document.addEventListener('hideFileUploadLoadingAnimation', onHideFileUpload)
		document.addEventListener('showAiUploadLoadingAnimation', onShowAiUpload)
		document.addEventListener('hideAiUploadLoadingAnimation', onHideAiUpload)

		window.addEventListener('openPlatformRequest', openPlatformRequestModal)
		window.addEventListener('openHubspotEngagement', openHubspotEngagementModal)

		let queryString = window.location.search
		let urlParams = new URLSearchParams(queryString)
		let requestId = urlParams.get('requestId')

		if (!requestId) {
			if (window.location.pathname === '/' || window.location.pathname === '/app') {
				// eslint-disable-next-line svelte/prefer-svelte-reactivity -- throwaway local builder; frameSrc is the reactive binding
				const url = new URL('/admin', getHrefReplaced())
				url.search = window.location.search
				frameSrc = url.toString()
			} else {
				// eslint-disable-next-line svelte/prefer-svelte-reactivity -- throwaway local builder; frameSrc is the reactive binding
				const url = new URL(
					(!window.location.pathname.startsWith('/app/') ? '/app' : '') + window.location.pathname,
					getHrefReplaced()
				)
				url.search = window.location.search
				frameSrc = url.toString()
			}
		}
		let fr: HTMLIFrameElement = top?.document?.getElementById('mainFrame') as HTMLIFrameElement
		if (fr) {
			if (requestId) {
				await fetchCachedRequestAndSubmitForm(requestId)
			} else {
				fr.src = frameSrc.toString()
			}
		} else {
			let ifrm = top?.document?.createElement('iframe')
			if (ifrm) {
				ifrm.src = frameSrc.toString()
				ifrm.style.width = '100%'
				ifrm.style.height = '100%'
				ifrm.allowFullscreen = true
				ifrm.id = 'mainFrame'
				ifrm.name = 'mainFrame'
				ifrm.title = 'Mineral Minds'
				top?.document.getElementById('mainFrameContainer')?.appendChild(ifrm)
			}
		}
	})

	onDestroy(() => {
		document.removeEventListener('showLoadingAnimation', onShowMmLoader)
		document.removeEventListener('hideLoadingAnimation', onHideMmLoader)
		document.removeEventListener('showFileUploadLoadingAnimation', onShowFileUpload)
		document.removeEventListener('hideFileUploadLoadingAnimation', onHideFileUpload)
		document.removeEventListener('showAiUploadLoadingAnimation', onShowAiUpload)
		document.removeEventListener('hideAiUploadLoadingAnimation', onHideAiUpload)

		window.removeEventListener('openPlatformRequest', openPlatformRequestModal)
		window.removeEventListener('openHubspotEngagement', openHubspotEngagementModal)
	})
</script>

<svelte:window on:message={handlePostMessages} />

<LoadingAnimations showOnMount={false} />
<HeadlessBadger />
<Basic bind:helpBoxOpen bind:iFrameUrl showLoadingAnimation={false} fullscreen>
	<div class="one-row-grid">
		<div class="skeleton-holder" class:iFrameLoaded>
			<LoadingSkeleton repeat={6} />
		</div>
		<iframe
			id="mainFrame"
			name="mainFrame"
			src={frameSrc}
			title="Mineral Minds"
			width="100%"
			height="100%"
			allowfullscreen
			on:load={() => setIFrameInfoAfterLoad()}
			data-hj-allow-iframe
		></iframe>
	</div>
	<PlatformRequestModal
		bind:this={platformRequestModal}
		lotId={reqLotId ?? ''}
		popId={reqPopId ?? ''}
	/>
	<HubSpotEngagementModal
		bind:this={hubspotEngagementModal}
		recordId={hubspotRecordId}
		type={hubspotEngagementType}
	/>
</Basic>
<!-- Hint for selenide tests -->
{#if iFrameLoaded}
	<div id="iFrameLoaded"></div>
{/if}

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';

	iframe {
		// https://stackoverflow.com/questions/21025319/iframe-creates-extra-space-below
		display: block;
	}

	#mainFrame {
		grid-row: 1/1;
	}

	.one-row-grid {
		display: grid;
		grid-template-columns: 1fr;
		width: 100%;
		height: 100%;
	}

	.skeleton-holder {
		/* above sirius page and fade out once sirius page has loaded */
		grid-row: 1/1;
		z-index: 100;
		position: absolute;

		@include global.flex-col;

		width: 100%;
		height: 100%;

		&.iFrameLoaded {
			opacity: 0;
			height: 0;
			visibility: hidden;
			transition:
				opacity 0.5s ease-in-out 0.2s,
				visibility 0s linear 0.7s;
		}
	}

	.prm-sirius-container {
		@include global.flex-row;

		flex-wrap: wrap;
		position: fixed;
		overflow-y: auto;
		z-index: 1000003; /* This will also overlay the Google Maps footer in the bottom right with z-index 1000001 */
		transition: opacity 0.5s ease-out;
		backdrop-filter: brightness(40%);
		inset: 0 0 0 0;
		padding: 2.5rem;
		--font-size: 11px;
	}

	@media (max-width: breakpoints.$small) {
		.prm-sirius-container {
			padding: 1.5rem;
		}
	}
</style>
